import {
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  ImportOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { message, Popconfirm, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { roleApi } from "api/role.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import {
  ActionButton,
  TableLayout,
  TableLayoutFooter,
  TableContent,
  getTableShowingRange,
} from "components/table";
import { userStore } from "store/userStore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { can } from "utils/auth";
import { QueryParam } from "types/query";
import { Permission, Role } from "types/role";
import { getTitle } from "utils";
import { RoleModal } from "./components/RoleModal";

const rolePath = "/role";

export const RolePage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 50,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Role[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingImport, setLoadingImport] = useState(false);
  const [originPermissions, setOriginPermissions] = useState<Permission[]>([]);

  const roleModalRef = useRef<RoleModal>(null);

  const debounceSearch = useCallback(
    debounce((keyword: string) => setQuery({ ...query, search: keyword, page: 1, limit: 20 }), 300),
    [setQuery, query],
  );

  useEffect(() => {
    document.title = getTitle(title);
    fetchOriginPermissions();
  }, []);

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchOriginPermissions = async () => {
    const res = await roleApi.getPermission();
    setOriginPermissions(res.data);
  };

  const fetchData = async () => {
    setLoading(true);
    const res = await roleApi.findAll(query);
    setLoading(false);
    setData(res.data);
    setTotal(res.data.length);
  };

  const handleImportRoutes = async () => {
    setLoadingImport(true);
    try {
      const res = await roleApi.importPermission();
      const { created, updated, removed } = res.data;
      message.success(`Import thành công: thêm ${created}, cập nhật ${updated}, xóa ${removed}`);
      await fetchOriginPermissions();
      fetchData();
    } finally {
      setLoadingImport(false);
    }
  };

  const handleDeleteRole = async (id: number) => {
    await roleApi.delete(id);
    fetchData();
    message.success("Xóa thành công");
  };

  const canCreateRole = can(rolePath, "CREATE");
  const canEditRole = can(rolePath, "UPDATE");
  const canDeleteRole = can(rolePath, "DELETE");

  const columns = useMemo<TableColumnsType<Role>>(
    () => [
      {
        title: "Quyền",
        dataIndex: "name",
        key: "name",
        width: 280,
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Mô tả",
        dataIndex: "description",
        key: "description",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      ...(canEditRole || canDeleteRole
        ? [
          {
            title: "Thao tác",
            key: "action",
            width: 80,
            align: "center" as const,
            render: (_: unknown, record: Role) => (
              <div className="flex justify-center items-center gap-2">
                {canEditRole && (
                  <Tooltip title="Cập nhật">
                    <ActionButton
                      aria-label="Cập nhật"
                      onClick={() => {
                        roleModalRef.current?.handleUpdate(record);
                      }}
                    >
                      <EditOutlined style={{ color: "#6c84e5" }} />
                    </ActionButton>
                  </Tooltip>
                )}

                {canDeleteRole && (
                  <Tooltip title="Xóa">
                    <Popconfirm
                      placement="top"
                      title={"Xác nhận xóa quyền này?"}
                      onConfirm={() => handleDeleteRole(record.id)}
                      okText="Xóa"
                      cancelText="Không"
                    >
                      <ActionButton aria-label="Xóa">
                        <DeleteOutlined style={{ color: "#ef4444" }} />
                      </ActionButton>
                    </Popconfirm>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
        : []),
    ],
    [canDeleteRole, canEditRole],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              placeholder: "Tìm quyền, mô tả",
              onChange: (value) => {
                setSearch(value);
                debounceSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearch("");
                        setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            leftActions={
              canCreateRole && (
                <AppButton
                  tone="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    roleModalRef.current?.handleCreate();
                  }}
                >
                  Thêm mới
                </AppButton>
              )
            }
            actions={
              userStore.info.role?.isAdmin ? (
                <AppButton
                  tone="light"
                  icon={<ImportOutlined />}
                  onClick={handleImportRoutes}
                  loading={loadingImport}
                >
                  Import routes
                </AppButton>
              ) : undefined
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<Role>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={({ limit, page }) => {
              setQuery((prev) => ({ ...prev, page, limit }));
            }}
          />
        }
      />

      <RoleModal onSubmitOk={fetchData} onClose={() => { }} ref={roleModalRef} />
    </div>
  );
});
